const RC = require("../resources/cats_enum.json");

import { getConvexHull2D } from '../hud/layers/levels/LevelUtils';
import {ElementFlags} from "../schema/dt-schema";
import {dummyMaterial, getBoxMesh} from "./box-mesh";

import { avp, THREE } from "@adsk/lmv";

const FOOTPRINT_CATEGORIES = [
	RC.Floors,
	RC.Roofs,
	RC.Rooms
];

const FOOTPRINT_CATEGORIES_FALLBACK = [
	RC.Ceilings,
	RC.Walls
];

const FOOTPRINT_CATEGORIES_HEIGHT = [
	RC.CurtainWallPanels,
	RC.CurtainWallMullions,
];

/**
 * @param {FacetNode} item
 */
export function createFloorFootprint(facility, item, cached) {

		const ext = facility.viewer.getExtension('Autodesk.Tandem.DtCompGeom');
		if (!ext) {
			// Nothing to show for the moment.
			cached.isComplete = false;
			return null;
		}

		//Do not show a boundary for elements without floor assigned.
		if (item.isUnassignedId) {
			cached.isComplete = false;
			return;
		}

		//Used when scaling coordinates to integer space for use by ClipperLib
		const SCALE = 128;

		let floorItem = item;
		let collected = collectFloorElements(facility, item);

		// A location level has no geometry of its own; its footprint is the union of its rooms'
		// geometry. When the node's own elements yield no floor geometry,
		// gather the level's room location elements and build the footprint from them.
		if (!collected.hasMatchingCategories && collected.floorSizeSum === 0) {
			const roomIds = collectLocationLevelRoomIds(facility, item.id);
			if (roomIds.size) {
				floorItem = { ids: roomIds };
				collected = collectFloorElements(facility, floorItem);
			}
		}

		const {
			allLoaded,
			allFloors,
			allFloorsFallback,
			floorSizeSum,
			fragIds,
			hasMatchingCategories
		} = collected;

		if (!allLoaded) cached.isComplete = false;

		//If there are no matching elements, just use bounding box of contained elements
		if (!hasMatchingCategories) {
			const box = getFloorBoxFuzzy(facility, floorItem, 0.9, false);
			if (!box) {
				cached.isComplete = false;
				return;
			}

			const mesh = getBoxMesh(box);

			mesh.name = "floor box" + item.id;
			cached.mesh = mesh;
			cached.isComplete = true;
			cached.isJustBox = true;
			return;
		}

		if (floorSizeSum === 0) {
			// Nothing to show, but do another pass in case new geoms are loaded
			// The expensive part is after this anyway
			cached.isComplete = false;
			return null;
		}

		/* Expensive part: generate a floor highlight mesh. */
		let footprint = new Autodesk.Tandem.DtCompGeom.MeshFootprint(SCALE);
		let totalBox = new THREE.Box3();

		let minElev = Infinity;
		let maxElev = -Infinity;
		let haveElev = false;

		for (let [model] of floorItem.ids) {
			const urn = model.urn();
			//TODO: need to take into account scaling as well, if there is any
			const offset = model.getData().placementWithOffset.elements[14];
			const levels = model.getLevels();
			for (let lid in levels) {
				const level = levels[lid];
				if (level.name === item.id && level.elev !== undefined && level.height !== undefined) {
					haveElev = true;
					if (minElev > level.elev + offset) {
						minElev = level.elev + offset;
					}
					if (maxElev < level.elev + offset + level.height) {
						maxElev = level.elev + offset + level.height;
					}
					break;
				}
			}

			const fl = model.getFragmentList();
			const it = model.getObjectTree();

			//Only process up to a reasonable number of floor elements so that we
			//don't block the application for too long.
			let elems = allFloors[urn];
			if (elems.length === 0) {
				elems = allFloorsFallback[urn];
			}
			elems.sort((a, b) => b.size - a.size);
			let numFloors = Math.min(100, elems.length);
			for (let i = 0; i < numFloors; i++) {
				let floor = elems[i];

				it.enumNodeFragments(floor.dbId, fid => {
					let mesh = fl.getVizmesh(fid);
					footprint.addMesh(mesh, true);
				}, true);
			}

			//Perform more expensive derivation of the level elevation and height from the bounding
			//box, if we don't have it from AEC model data
			if (!haveElev) {
				let fbox = model.getFuzzyBox({allowlist: fragIds[urn], quantil: 0.75});
				totalBox.union(fbox);
			}
		}

		let complexPolygon = footprint.toComplexPolygon(1.5, true);

		if (!complexPolygon) {
			return null;
		}

		complexPolygon.triangulate();

		if (!haveElev) {
			minElev = totalBox.min.z;
			maxElev = totalBox.max.z;
		}

		// extrude for given thickness
		const geom = complexPolygon.toExtrudedMesh(maxElev - minElev);
		if (geom) {

			//This can be used in case the density of vertical edge segments is considered objectionable
			//geom.iblines = null;
			//createWireframe(geom, null, geom.boundingBox, false);

			const mesh = new THREE.Mesh(geom, dummyMaterial);
			mesh.matrix.elements[14] = maxElev; //Raise the extruded mesh to top out at the correct max elevation
			mesh.matrixWorld.elements[14] = maxElev;
			mesh.matrixAutoUpdate = false;
			mesh.matrixWorldNeedsUpdate = false;
			mesh.name = "floor " + item.id;
			cached.mesh = mesh;
		}

		return null;
}


const StairRailings = [RC.Railings, RC.RailingHandRail, RC.RailingHandRailAboveCut, RC.RailingTopRailAboveCut,
	RC.StairsRailing, RC.StairsRailingBaluster, RC.StairsRailingRail];

/**
 * @param {DtFacility} facility
 * @param {FacetNode} item
 */
export function getFloorBoxFuzzy(facility, item, quantile, excludeGhosted) {

	let totalBox = new THREE.Box3();

	const options = { allowlist: null, quantil: quantile || 0.9, useOriginalBounds: true};

	for (let [model, ids] of item.ids) {
		const fl = model.getFragmentList();
		const it = model.getObjectTree();

		// this method could be called on model unloading due to fired "GEOMETRY_LOADED_EVENT" event
		if (!it) {
			continue;
		};

		const dbId2topLevel = model.getData().dbId2topLevel;
		const dbId2catId = model.getData().dbId2catId;

		let fragIds = [];

		for (let dbId of ids) {

			//Do not include elements that have a top constraint -- those will mess up the height
			if (dbId2topLevel[dbId]) {
				continue;
			}

			//exclude stairs, because they often cross floors in a way that screws up the bounds
			if (StairRailings.includes(dbId2catId[dbId])) {
				continue;
			}

			it.enumNodeFragments(dbId, fid => {

				if (excludeGhosted && !fl.isFragVisible(fid)) {
					return;
				}

				fragIds.push(fid);
			}, false);
		}

		if (fragIds.length === 0) {
			continue;
		}

		options.allowlist = fragIds;
		let fbox = model.getFuzzyBox(options);
		totalBox.union(fbox);
	}

	if (totalBox.isEmpty()) {
		return null;
	}

	return totalBox;
}

// A location level has no geometry of its own; its floor footprint is the union of its rooms'
// geometry. Location room elements do carry geometry in the default model, so gather the ones
// whose QC.Parent points at the level with the given name and
// return them as an ids-by-model map ready for collectFloorElements.
function collectLocationLevelRoomIds(facility, levelName) {
	const idsByModel = new Map();

	const defaultModel = facility.getDefaultModel?.();
	if (!defaultModel || !levelName) {
		return idsByModel;
	}

	const levels = defaultModel.getLocationLevels?.();
	const rooms = defaultModel.getLocationRooms?.();
	if (!levels || !rooms) {
		return idsByModel;
	}

	// dbId(s) of the location level(s) with this name
	const levelDbIds = new Set();
	for (const level of Object.values(levels)) {
		if (level?.name === levelName && level.dbId) {
			levelDbIds.add(level.dbId);
		}
	}

	if (!levelDbIds.size) {
		return idsByModel;
	}

	// room location elements whose QC.Parent points at one of those levels
	const roomDbIds = [];
	for (const room of Object.values(rooms)) {
		const parentDbId = defaultModel.getLocationParentDbId(room.dbId);
		if (parentDbId && levelDbIds.has(parentDbId)) {
			roomDbIds.push(room.dbId);
		}
	}

	if (roomDbIds.length) {
		idsByModel.set(defaultModel, roomDbIds);
	}

	return idsByModel;
}

function collectFloorElements(facility, item) {
	let elementBox = new THREE.Box3();
	let tmpBox = new THREE.Box3();
	let tmpVec = new THREE.Vector3();

	const collect = {
		allFloors: {},
		allFloorsFallback: {},
		fragIds: {},
		floorSizeSum: 0,
		allLoaded: true,
		hasMatchingCategories: false
	};

	// A synthesized location level node may carry no assigned elements (no `ids`); treat as empty so
	// callers fall back to the level's rooms' geometry / bounding box instead of throwing.
	for (let [model, ids] of item.ids) {
		const modelData = model.getData();
		if (!modelData) return collect;

		const fl = model.getFragmentList();
		const it = model.getObjectTree();

		// this method could be called on model unloading due to fired "GEOMETRY_LOADED_EVENT" event
		if (!it) return collect;

		const { dbId2catId, dbId2topLevel, dbId2flags } = modelData;

		const urn = model.urn();
		const fragIds = collect.fragIds[urn] = [];

		//Collect all the floor elements first -- there can sometimes be many thousands
		//of irrelevant "floors" that are really carpets etc, and we need to ignore these
		//for the sake of efficiency
		const allFloors = collect.allFloors[urn] = [];
		const allFloorsFallback = collect.allFloorsFallback[urn] = [];

		for (let dbId of ids) {

			//Do not include elements that have a top constraint -- those will mess up the height
			if (dbId2topLevel[dbId]) {
				continue;
			}

			//Exclude parent curtain wall objects (that can span many floors but without explicit top constraint in case of IFC source)
			if (dbId2flags[dbId] === ElementFlags.CompositeParent) {
				continue;
			}

			let catId = dbId2catId[dbId];

			elementBox.makeEmpty();

			const isPrimaryFootprintCat = FOOTPRINT_CATEGORIES.includes(catId);
			const isSecondaryFootprintCat = FOOTPRINT_CATEGORIES_FALLBACK.includes(catId);
			const isHeightCat = FOOTPRINT_CATEGORIES_HEIGHT.includes(catId);

			// We can be more lenient due to the faster nature of the computation.
			if (isPrimaryFootprintCat || isSecondaryFootprintCat || isHeightCat) {

				collect.hasMatchingCategories = true;

				it.enumNodeFragments(dbId, fid => {

					fragIds.push(fid);

					if (!isHeightCat) {
						fl.getWorldBounds(fid, tmpBox);
						elementBox.union(tmpBox);
					}

					// Check if this pass' result is final (all floor geometries are loaded)
					const isLoaded = !!fl.getGeometry(fid);
					if (collect.allLoaded && !isLoaded) {
						collect.allLoaded = false;
					}
				}, true);
			}

			if (!elementBox.isEmpty()) {
				const floorSize = elementBox.size(tmpVec);
				const area = floorSize.x * floorSize.y;

				if (isPrimaryFootprintCat) {
					allFloors.push({dbId, size: area});
				} else if (isSecondaryFootprintCat) {
					allFloorsFallback.push({dbId, size: area});
				}
				collect.floorSizeSum += area;
			}
		}
	}

	return collect;
}

export function tryFloorConvexHull(facility, item, state) {
	// Assume complete unless we find otherwise.
	state.isComplete = true

	// A synthesized location level node may carry no assigned elements of its own.
	let floorItem = item.ids ? item : { ...item, ids: new Map() };
	let collected = collectFloorElements(facility, floorItem);

	// A location level has no geometry of its own; build its hull from its rooms' geometry
	// (mirrors createFloorFootprint) when the level's own elements yield no floor geometry.
	if (!collected.hasMatchingCategories && collected.floorSizeSum === 0) {
		const roomIds = collectLocationLevelRoomIds(facility, item.id);
		if (roomIds.size) {
			floorItem = { ids: roomIds };
			collected = collectFloorElements(facility, floorItem);
		}
	}

	const {
		allLoaded,
		allFloors,
		allFloorsFallback,
		floorSizeSum,
		hasMatchingCategories
	} = collected;

	if (!allLoaded) state.isComplete = false;
	if (floorSizeSum === 0 && hasMatchingCategories) {
		// Nothing to show, but do another pass in case new geoms are loaded
		// The expensive part is after this anyway
		state.isComplete = false;
		return state;
	}

	let pts = [];
	let minZ = Infinity;
	for (let [model] of floorItem.ids) {
		const fl = model.getFragmentList();
		const it = model.getObjectTree();

		const urn = model.urn();
		// this method could be called on model unloading due to fired "GEOMETRY_LOADED_EVENT" event
		if (!allFloors[urn]) { continue; }

		// Only process up to a reasonable number of floor elements so that we don't block the application for too long.
		let elems = allFloors[urn].concat(allFloorsFallback[urn]);
		elems.sort((a, b) => b.size - a.size);

		let numFloors = Math.min(100, elems.length);
		for (let i = 0; i < numFloors; i++) {
			it.enumNodeFragments(elems[i].dbId, fid => {
				let mesh = fl.getVizmesh(fid);
				if (!mesh.geometry) return;

				avp.VertexEnumerator.enumMeshVertices(mesh.geometry, (v) => {
					pts.push({ x: v.x, y: v.y });
					if (v.z < minZ) minZ = v.z;
				}, mesh.matrixWorld);
			}, true);
		}
	}

	//Fall back to bounding box of contained elements if no matching building categories
	if (pts.length === 0) {
		const box = getFloorBoxFuzzy(facility, floorItem, 0.9, false);

		if (!box) {
			// Nothing to show, but do another pass in case new geoms are loaded
			// The expensive part is after this anyway
			state.isComplete = false;
			return state;
		}

		pts.push({x: box.min.x, y: box.min.y, z: box.min.z});
		pts.push({x: box.max.x, y: box.min.y, z: box.min.z});
		pts.push({x: box.max.x, y: box.max.y, z: box.min.z});
		pts.push({x: box.min.x, y: box.max.y, z: box.min.z});

		pts.push({x: box.min.x, y: box.min.y, z: box.max.z});
		pts.push({x: box.max.x, y: box.min.y, z: box.max.z});
		pts.push({x: box.max.x, y: box.max.y, z: box.max.z});
		pts.push({x: box.min.x, y: box.max.y, z: box.max.z});

		minZ = box.min.z;
	}

	state.hull = getConvexHull2D(pts);
	state.minZ = minZ;

	return state;
}
